import { Link, useRouterState } from '@tanstack/react-router';
import { DiamondLogo } from '../ui/DiamondLogo.js';
import { trpc } from '../../lib/trpc.js';
import { clsx } from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';

const NAV_LINKS = [
  { to: '/pulse', label: 'Pulse' },
  { to: '/calendar', label: 'Calendar' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/trends', label: 'Trends' },
  { to: '/timesheets', label: 'Timesheets' },
  { to: '/payouts', label: 'Payouts' },
  { to: '/technicians', label: 'Crew' },
  { to: '/creatives', label: 'Creatives' },
  { to: '/leads', label: 'Leads' },
  { to: '/bookings', label: 'Bookings' },
  { to: '/advisor', label: 'Advisor' },
  { to: '/upload', label: 'Upload CSV' },
  { to: '/pipeline', label: 'Pipeline' },
  { to: '/settings', label: 'Settings' },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { data: user } = trpc.auth.me.useQuery();
  const logoutMutation = trpc.auth.logout.useMutation({ onSuccess: () => { window.location.href = '/login'; } });
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const now = new Date();
  const londonDate = formatInTimeZone(now, 'Europe/London', 'EEEE, d MMMM yyyy');

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-navy text-cream border-b border-navy-soft">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DiamondLogo size={28} />
            <span className="font-display text-xl font-medium text-cream">Cockpit</span>
            <span className="hidden sm:block text-gold text-xs font-body font-medium ml-2 opacity-70">{londonDate}</span>
          </div>
          {user && (
            <div className="flex items-center gap-4">
              <span className="text-xs text-cream/70 hidden sm:block">{user.name}</span>
              <button
                onClick={() => logoutMutation.mutate()}
                className="text-xs text-cream/70 hover:text-cream transition-colors"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Nav */}
      <nav className="bg-navy-deep border-b border-navy-soft/50 overflow-x-auto">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-1 h-10 whitespace-nowrap">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded transition-colors',
                  currentPath.startsWith(link.to)
                    ? 'bg-gold text-navy'
                    : 'text-cream/70 hover:text-cream hover:bg-navy-soft'
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}
